/* eslint-disable */
import DialogProvider from "@components/dialog_provider"
import TextInput from "@components/text_input"
import { INPUT_LABEL_BLUR, PRIMARY } from "@constants/colors"
import { useCouponsPageContext } from "@contexts/main/coupons_page_context"
import { createCoupons, getCustomers } from "@domain/coupons"
import { yupResolver } from "@hookform/resolvers/yup"
import useControlledForm from "@hooks/use_controlled_form"
import { Box, InputLabel, Stack, Typography } from "@mui/material"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import { DatePicker } from "@mui/x-date-pickers/DatePicker"
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider/LocalizationProvider"
import { useQueryClient } from "@tanstack/react-query"
import { showErrorToast } from "@utils/toast"
import {
  CouponCode,
  CouponUsageType,
  CouponValueType,
  CustomerPerson
} from "__generated__/graphql"
import dayjs from "dayjs"
import "dayjs/locale/tr"
import { useEffect, useState } from "react"
import type { GroupBase, OptionsOrGroups } from "react-select"
import * as yup from "yup"

export type OptionType = { value: string; label: string }
export type loadOptionData = {
  options: { value: string; label: string }[]
  hasMore: boolean
}

export type CouponFormData = {
  name: string
  valueType: string
  value: number
  maxRedemptions: number
  usageLimit: number
  usageType: string
  expireAt?: Date
  customer?: string
}

export default function CreateCoupon({
  isOpen,
  setClose
}: {
  isOpen: boolean
  setClose: () => void
}) {
  const { translate, setCouponsData } = useCouponsPageContext()
  const queryClient = useQueryClient()

  const schema = yup
    .object({
      name: yup
        .string()
        .min(2, translate("first_name_must_be_atleast_2_characters"))
        .required(translate("first_name_field_is_required")),
      valueType: yup.string().required(),
      value: yup.number().required(),
      maxRedemptions: yup.number().required(),
      usageLimit: yup.number().required(),
      usageType: yup.string().required(),
      expireAt: yup.date(),
      customer: yup.string()
    })
    .required()

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { isValid }
  } = useControlledForm<CouponFormData>({
    resolver: yupResolver(schema),
    mode: "all"
  })

  const [loading, setLoading] = useState(false)

  useEffect(() => {
    setValue("expireAt", new Date(new Date().getFullYear(), 11, 31))
  }, [isOpen])

  function handleClose() {
    if (loading) {
      return
    }
    setClose()
    reset()
  }

  async function onSubmit(data: CouponFormData) {
    setLoading(true)
    const {
      isSuccess,
      errorMessage,
      data: responseData
    } = await createCoupons({
      name: data.name,
      value: data.value,
      valueType: data.valueType as CouponValueType,
      maxRedemptions: data.maxRedemptions,
      usageType: data.usageType as CouponUsageType,
      expiredAt: data.expireAt,
      usageLimit: data.usageLimit,
      customer: data.customer
    })
    if (!isSuccess || !responseData) {
      setLoading(false)
      showErrorToast(String(errorMessage))
      return
    }
    const newCoupon = responseData.data as CouponCode

    setCouponsData?.((prevUsersData) => [newCoupon, ...prevUsersData])

    queryClient.invalidateQueries({ queryKey: ["coupons-data-table"] })
    setLoading(false)
    handleClose()
  }

  const [openFrom, setOpenFrom] = useState(false)
  const [isDateFocus, setIsDateFocus] = useState(false)
  const [dataLoading, setDataLoading] = useState(false)

  async function loadCustomers(
    search: string,
    prevOptions: OptionsOrGroups<OptionType, GroupBase<OptionType>>
  ): Promise<loadOptionData> {
    setDataLoading(true)

    try {
      const pageNumber =
        prevOptions.length === 0 ? 1 : Math.ceil(prevOptions.length / 10) + 1

      const { data, isSuccess } = await getCustomers({
        pageSize: 10,
        search: search.length >= 3 ? search : undefined,
        pageNumber
      })

      if (!isSuccess) {
        throw new Error(String())
      }

      const response = data as CustomerPerson[]

      const customers = response.map((cPerson) => ({
        value: cPerson?.customer,
        label: `${cPerson?.firstName} ${cPerson?.lastName}`
      }))

      setDataLoading(false)

      return {
        options: customers || [],
        //hasMore: response.hasNextPage
        hasMore: false
      }
    } catch (error) {
      setDataLoading(false)
      showErrorToast("error.message")
      return { options: [], hasMore: false }
    }
  }

  return (
    <DialogProvider
      title={translate("create_coupon")}
      leftButton={{
        text: translate("cancel"),
        color: "secondary",
        onClick: setClose,
        disabled: loading
      }}
      rightButton={{
        text: translate("create"),
        color: "primary",
        onClick: handleSubmit(onSubmit),
        loading: loading,
        disabled: !isValid
      }}
      isOpen={isOpen}
      setClose={handleClose}
      size="small"
    >
      <Stack gap={1}>
        <TextInput<CouponFormData>
          label={translate("name")}
          disabled={loading}
          name="name"
          placeholder={translate("name")}
          control={control}
          required
        />
        <TextInput<CouponFormData>
          type="select"
          label={translate("value_type")}
          disabled={loading}
          name="valueType"
          control={control}
          placeholder={translate("value_type")}
          selectOptions={[
            { label: "Percentage", value: "PERCENT" },
            { label: "Amount", value: "AMOUNT" }
          ]}
          required
        />
        <TextInput<CouponFormData>
          type="number"
          disabled={loading}
          name="value"
          control={control}
          label={translate("value")}
          placeholder={translate("value")}
          required
        />
        <TextInput<CouponFormData>
          type="number"
          name="maxRedemptions"
          disabled={loading}
          label={translate("max_redemptions")}
          placeholder={translate("max_redemptions")}
          control={control}
          required
        />
        <TextInput
          type="select"
          disabled={loading}
          name="usageType"
          control={control}
          label={translate("usage_type")}
          placeholder={translate("usage_type")}
          selectOptions={[
            { label: "Monthly", value: "MONTHLY" },
            { label: "Yearly", value: "YEARLY" },
            { label: "Monthly and Yearly", value: "MONTHLYANDYEARLY" },
            { label: "AI Assistant", value: "AIASSISTANT" }
          ]}
          required
        />
        <TextInput
          type="number"
          disabled={loading}
          name="usageLimit"
          control={control}
          label={translate("usage_limit")}
          placeholder={translate("usage_limit")}
          required
        />
        <Stack>
          <InputLabel
            sx={{
              color: isDateFocus ? PRIMARY : INPUT_LABEL_BLUR,
              alignItems: "center"
            }}
          >
            <Typography>{translate("expire_at")}</Typography>
          </InputLabel>
          <Box
            sx={{
              border: "1px solid #DDDDDD",
              borderRadius: 1,
              ":hover": {
                border: "1px solid #000000"
              }
            }}
          >
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="tr">
              <DatePicker
                format="DD.MM.YYYY"
                sx={{
                  "& input": { width: "100%" },
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      border: "none"
                    }
                  },
                  width: "100%"
                }}
                disablePast
                disableOpenPicker
                open={openFrom}
                onClose={() => setOpenFrom(false)}
                slotProps={{
                  textField: {
                    size: "small",
                    onClick: () => setOpenFrom(true),
                    onFocus: () => setIsDateFocus(true),
                    onBlur: () => setIsDateFocus(false)
                  },
                  field: {
                    clearable: true,
                    onClear: () => setValue("expireAt", undefined)
                  }
                }}
                onChange={(date) => {
                  if (date) {
                    setValue("expireAt", date as unknown as Date)
                  }
                }}
                defaultValue={dayjs(new Date(new Date().getFullYear(), 11, 31))}
              />
            </LocalizationProvider>
          </Box>
        </Stack>
        {/* <MultiSelectAsync
          label={translate("customer")}
          placeholder={translate("customer")}
          name="customer"
          control={control}
          loadOptions={loadCustomers}
          isLoading={dataLoading}
          isMulti={false}
        /> */}
      </Stack>
    </DialogProvider>
  )
}
